/**
 * SettingsOverview — แท็บ "ภาพรวม" ของหน้าตั้งค่า (Non-HR UX §6, แก้ feedback ข้อ 3).
 *
 * ปัญหา: ตั้งค่ากระจายหลายที่ (Settings tabs + Time hub + Wage hub + People) →
 * ผู้ใช้ไม่เห็นทั้งหมด ไม่มั่นใจว่าตั้งครบ.
 *
 * แก้: หน้าเดียวเห็น "ทุกหมวดตั้งค่า + สถานะ" — หมวดหลักโชว์ ✓/ยังไม่ตั้ง (จาก
 * setup checklist จริง) ส่วนที่เหลือเป็นทางลัดเข้าไปตั้ง. แตะหมวด = ไปหน้านั้น.
 */
import { Link } from 'react-router-dom';
import {
  CheckCircle2, AlertTriangle, ChevronRight,
  Building2, CalendarClock, MapPin, Shield, Sparkles,
  Users, Wallet, BarChart3, GitBranch,
  Clock, CalendarOff, Plane, Timer,
  PlusCircle, MinusCircle, Percent, LayoutTemplate, FileText,
} from 'lucide-react';
import { useSetupChecklist } from '../hooks';

type Area = {
  label: string;
  route: string;
  icon: typeof Building2;
  /** ผูกกับ setup checklist key เพื่อโชว์ ✓/ยังไม่ตั้ง */
  setupKey?: string;
};

const GROUPS: Array<{ title: string; areas: Area[] }> = [
  {
    title: 'บริษัท & ระบบ',
    areas: [
      { label: 'ข้อมูลบริษัท', route: '/owner/settings#company', icon: Building2 },
      { label: 'รอบเงินเดือน (วันตัดรอบ)', route: '/owner/settings#payroll', icon: CalendarClock },
      { label: 'เช็คอิน (ตำแหน่ง/รัศมี)', route: '/owner/settings#checkin', icon: MapPin },
      { label: 'ความปลอดภัย', route: '/owner/settings#security', icon: Shield },
      { label: 'ผู้ช่วย AI', route: '/owner/settings#ai', icon: Sparkles },
    ],
  },
  {
    title: 'คน & สิทธิ์',
    areas: [
      { label: 'พนักงาน', route: '/hr/people', icon: Users, setupKey: 'employees' },
      { label: 'ฐานเงินเดือนทุกคน', route: '/hr/people', icon: Wallet, setupKey: 'salary' },
      { label: 'สิทธิ์การลา (โควต้า)', route: '/hr/quotas', icon: BarChart3, setupKey: 'quota' },
      { label: 'สายอนุมัติ (ผู้อนุมัติ)', route: '/hr/people', icon: GitBranch, setupKey: 'approval' },
    ],
  },
  {
    title: 'เวลา & ลา',
    areas: [
      { label: 'กะการทำงาน', route: '/hr/time#shifts', icon: Clock },
      { label: 'วันหยุดประจำปี', route: '/hr/time#holidays', icon: CalendarOff, setupKey: 'holidays' },
      { label: 'แพ็กเกจการลา', route: '/hr/time#leave', icon: Plane },
      { label: 'สูตรเวลา / สายงาน', route: '/hr/time#settings', icon: Timer },
    ],
  },
  {
    title: 'ค่าจ้าง',
    areas: [
      { label: 'เงินเพิ่ม (เบี้ยเลี้ยง)', route: '/owner/wage#allowance', icon: PlusCircle },
      { label: 'เงินหัก', route: '/owner/wage#deduction', icon: MinusCircle },
      { label: 'คอมมิชชั่น', route: '/owner/wage#commission', icon: Percent },
    ],
  },
  {
    title: 'เริ่มเร็ว',
    areas: [
      { label: 'ตัวช่วยตั้งค่าระบบ (Setup Wizard)', route: '/owner/setup', icon: Sparkles },
      { label: 'ตั้งค่าสำเร็จรูปตามอุตสาหกรรม (Template)', route: '/owner/templates', icon: LayoutTemplate },
    ],
  },
];

export function SettingsOverview() {
  const { data } = useSetupChecklist();
  const doneByKey = new Map((data?.items ?? []).map((i) => [i.key, i.done]));
  const tracked = data?.items.length ?? 0;
  const trackedDone = (data?.items ?? []).filter((i) => i.done).length;
  const pct = data?.pct ?? 0;

  return (
    <div className="space-y-5 max-w-2xl">
      {/* ความพร้อม */}
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-2">
          <h2 className="font-display font-semibold">ความพร้อมระบบ</h2>
          <span className="ml-auto text-sm font-medium text-brand-700">ตั้งค่าหลักครบ {trackedDone}/{tracked} · {pct}%</span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div className="h-full bg-brand-gradient transition-all" style={{ width: `${pct}%` }} />
        </div>
        {data && !data.complete && data.nextStep && (
          <Link to={data.nextStep.route} className="btn-cta w-full justify-center flex mt-3">
            ทำต่อ: {data.nextStep.label}
          </Link>
        )}
        {data?.complete && (
          <p className="text-sm text-brand-700 flex items-center gap-1.5 mt-2">
            <CheckCircle2 className="w-4 h-4" /> ตั้งค่าหลักครบแล้ว พร้อมทำเงินเดือน
          </p>
        )}
      </div>

      {/* พิมพ์เขียว — อ่านสรุปการตั้งค่าทั้งบริษัทในหน้าเดียว (สำหรับ HR ที่เพิ่งเข้ามา) */}
      <Link to="/owner/blueprint" className="card p-4 flex items-center gap-3 hover:bg-gray-50 transition">
        <FileText className="w-6 h-6 text-teal shrink-0" />
        <div className="flex-1">
          <p className="font-medium text-gray-900">พิมพ์เขียวการทำเงินเดือน</p>
          <p className="text-xs text-gray-500">อ่านสรุปทั้งระบบในหน้าเดียว — รอบจ่าย ค่าจ้าง เงินเพิ่ม-หัก กะ วันหยุด ผู้อนุมัติ (ให้ HR เข้าใจตรงกันโดยไม่ต้องถามซ้ำ)</p>
        </div>
        <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
      </Link>

      {/* แผนที่ตั้งค่าทั้งหมด */}
      <p className="text-sm text-gray-500">ตั้งค่าทั้งหมดอยู่ที่นี่ — แตะหมวดเพื่อเข้าไปตั้ง</p>
      {GROUPS.map((g) => (
        <div key={g.title} className="space-y-1.5">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{g.title}</h3>
          <div className="card divide-y divide-gray-100">
            {g.areas.map((a) => {
              const Icon = a.icon;
              const tracked = a.setupKey !== undefined;
              const done = tracked ? doneByKey.get(a.setupKey!) === true : undefined;
              return (
                <Link key={a.label} to={a.route} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition first:rounded-t-xl last:rounded-b-xl">
                  <Icon className="w-5 h-5 text-brand-600 shrink-0" />
                  <span className="text-sm font-medium flex-1">{a.label}</span>
                  {tracked && (
                    done ? (
                      <span className="inline-flex items-center gap-1 text-xs text-brand-600 shrink-0">
                        <CheckCircle2 className="w-4 h-4" /> ตั้งแล้ว
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-gold-deep shrink-0">
                        <AlertTriangle className="w-4 h-4" /> ยังไม่ตั้ง
                      </span>
                    )
                  )}
                  <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
